import type { AccountRow } from '@shared/flow/account-view';
import { joinNonBlank } from '@shared/flow/non-blank';
import { parseDomainLink } from '../links';
import { parseCalendarDay } from '../../../lib/calendarDay';
import type { EntityField, EntityFieldSection } from '../shell-next/entity/entityFieldTypes';

/**
 * Pure account field formatters + the `AccountFieldsCard` row table, lifted
 * out of `AccountDetailPanels.tsx` (bead terra-design-v2-drilldowns-ppn.4,
 * U1d, plan 005 §2 + plan 004 §2). `AccountFieldsCard` keeps computing its
 * own JSX from these same account fields (V1 behaviour is byte-identical,
 * zero test edits) — `accountFieldSections`/`accountLongTextBlocks` below
 * are the NEW data-shaped equivalents the V2 band (`AccountInfoCard`, U4) and
 * lens (`AccountFieldsColumn`, U7b) render from, so those two surfaces can
 * never hand-roll a second, driftable copy of this row table.
 *
 * No import from `shell-next/**` other than the pure `entityFieldTypes`
 * (types only, no runtime) — `kv`'s null rule is defined here, and U4's
 * `bandFields.ts` re-exports it, so the dependency points V2 → V1-pure,
 * never back (Do NOT Create list).
 */

// ── Formatters ──────────────────────────────────────────────────────────────

/**
 * timestamptz ONLY (reads the instant in the viewer's local day). A Postgres DATE
 * must use `fmtDateOnly` in `../cells` instead — see
 * src/services/flow/query/pg-date-column-types.ts.
 */
export function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** A bare calendar day — what `dateOnlyDay` produces; never a timestamptz. */
export const BARE_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A Postgres DATE (wire `YYYY-MM-DD` or `…T00:00:00.000Z`) as a bare `YYYY-MM-DD`, so a downstream string formatter can tell it from an instant. */
export function dateOnlyDay(value: string | null | undefined): string | null {
  const m = value ? /^(\d{4}-\d{2}-\d{2})/.exec(value) : null;
  return m ? m[1]! : null;
}

/** `fmtDateOnly` as text: the compact local date, the raw string if it won't parse, null when missing. */
export function fmtDateOnlyText(
  value: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string | null {
  if (!value) return null;
  const d = parseCalendarDay(value);
  return d ? d.toLocaleDateString(undefined, opts) : value;
}

/**
 * "$1.23M", "$380K", "$950", or null for a missing amount (each caller keeps
 * its own empty glyph): ARR-style money. Billions and millions keep up to two
 * decimals with trailing zeros dropped ("$1.5B", "$1.1M", "$1M"). Thousands
 * round to a whole number ("$380K"). Under a thousand the whole dollars show.
 * The unit is chosen after rounding, as `formatCompactCount` does, so 999,999
 * is "$1M" (not "$1000K") and 999.5 is "$1K" (not "$1,000"). Rounding works on
 * the magnitude so a negative amount rounds away from zero like its positive
 * twin ("-$2K" for -1,500), and a value that rounds to zero never prints a
 * minus.
 *
 * A number is typed to a string, so a caller that holds a finite amount (a sum
 * of finite values) needs no empty-glyph fallback. A non-finite number still
 * yields null at runtime, which no such caller can produce.
 */
export function money(v: number): string;
export function money(v: number | null | undefined): string | null;
export function money(v: number | null | undefined): string | null {
  if (v == null || !Number.isFinite(v)) return null;
  const abs = Math.abs(v);
  const magnitude = moneyMagnitude(abs);
  return `${v < 0 && magnitude !== '0' ? '-' : ''}$${magnitude}`;
}

function moneyMagnitude(abs: number): string {
  if (Math.round(abs / 1e6) >= 1000) return `${(abs / 1e9).toFixed(2).replace(/\.?0+$/, '')}B`;
  if (Math.round(abs / 1e3) >= 1000) return `${(abs / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (Math.round(abs) >= 1000) return `${Math.round(abs / 1e3)}K`;
  return String(Math.round(abs));
}

export function num(v: number | null): string | null {
  if (v == null) return null;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

export function yesNo(v: boolean | null): string | null {
  return v == null ? null : v ? 'Yes' : 'No';
}

/** The `replace(/^https?:\/\//, '')` used four times for the GTME/CDR
 * meeting links, plus the primary-workspace link — strips only the
 * protocol, preserving path/query. */
export function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  return url.replace(/^https?:\/\//, '');
}

/** Joins the populated HQ parts with `', '`; `null` when none are populated
 * (`AccountDetailPanels.tsx`'s inline `[city, state, country].filter(Boolean)
 * .join(', ') || null`). */
export function joinHq(city: string | null, state: string | null, country: string | null): string | null {
  return [city, state, country].filter(Boolean).join(', ') || null;
}

/** One resolved Customer Lookalikes slot — dangling slots (target absent
 * from the mirror, `_name` null) are filtered out; see `resolvedLookalikes`. */
export interface ResolvedLookalike {
  id: string;
  name: string;
  useCase: string | null;
}

/**
 * Customer Lookalikes (U5a Decision 7 / U6, R5): slot N is null-name whenever
 * the target account is absent from the mirror (dangling `_id`) — skip those
 * rather than rendering a bare id. Returns `[]` when no slot resolves to a
 * name (fill rate ~22%, plan Approach note) — the section hides entirely.
 */
export function resolvedLookalikes(account: AccountRow): ResolvedLookalike[] {
  return [
    { id: account.account_lookalike_1_id, name: account.account_lookalike_1_name, useCase: account.account_lookalike_1_use_case ?? null },
    { id: account.account_lookalike_2_id, name: account.account_lookalike_2_name, useCase: account.account_lookalike_2_use_case ?? null },
    { id: account.account_lookalike_3_id, name: account.account_lookalike_3_name, useCase: account.account_lookalike_3_use_case ?? null },
  ].filter((l): l is ResolvedLookalike => !!l.id && !!l.name);
}

// ── `kv` — the shared null/empty-value rule (plan 004 D4) ───────────────────
//
// `null`/`''` → a muted em dash; `0` (and any other falsy-but-real value)
// renders as-is. This IS `isEmptyFieldValue`'s complement
// (`shell-next/entity/entityFieldTypes.ts:57`) — defined here, not imported,
// per the Do NOT Create list (no import from `shell-next/**` into this
// V1-pure module; U4's `bandFields.ts` re-exports this `kv`, so the
// dependency points V2 → V1-pure, never back).

export const EMPTY_VALUE = '—';

/** Builds one `EntityField` row, applying the `kv` null rule: `null`/`''` →
 * `{ value: '—', muted: true }`; every other value (including `0`) renders
 * as given. */
export function kv(key: string, label: string, value: EntityField['value'], href?: string): EntityField {
  if (value == null || value === '') {
    return { key, label, value: EMPTY_VALUE, muted: true };
  }
  return href ? { key, label, value, href } : { key, label, value };
}

// ── `accountFieldSections` — AccountFieldsCard's row table as data ──────────

/**
 * The complete synced field surface, in `AccountFieldsCard`'s production
 * section/row order (plan 005 Behavior Spec 1). `account_primary_workspace`
 * is the only conditional section — appended only when populated (fill rate
 * ~78%, plan Approach note), matching how `AccountFieldsCard` hides every
 * other empty section.
 */
export function accountFieldSections(account: AccountRow): EntityFieldSection[] {
  const domain = parseDomainLink(account.email_domain_link);

  const sections: EntityFieldSection[] = [
    {
      key: 'company',
      heading: 'Company',
      fields: [
        kv('type', 'Type', account.account_type),
        kv('industry', 'Industry', account.industry),
        kv('employees', 'Employees', num(account.number_of_employees)),
        kv('segment', 'Segment', account.company_segment),
        kv('hq', 'HQ', joinHq(account.billing_city, account.billing_state, account.billing_country)),
        kv('domain', 'Domain', domain?.label ?? null, domain?.href),
        kv('sales-tier', 'Sales Tier', account.sales_tier),
        kv('account-fit', 'Account Fit', account.account_fit),
        kv('f500-rank', 'F500 Rank', num(account.fortune_500_rank)),
      ],
    },
    {
      key: 'pipeline-commercial',
      heading: 'Pipeline & Commercial',
      fields: [
        kv('pcv', 'PCV', money(account.pcv)),
        kv('arr', 'ARR', money(account.arr)),
        kv('arr-opportunity', 'ARR Opportunity', money(account.arr_opportunity)),
        kv('open-pipeline', 'Open Pipeline', money(account.open_pipeline)),
        kv('open-opps', '# Open Opps', num(account.number_of_open_opp)),
        kv('won-opps', '# Won Opps', num(account.number_of_won_opp)),
        kv('open-renewals', '# Open Renewals', num(account.number_of_open_renewals)),
        kv('new-biz-opps', '# New-Biz Opps', num(account.number_of_open_new_biz_opps)),
        kv('pipeline-stage', 'Pipeline Stage', account.last_opportunity_stage),
        kv('product', 'Product', account.product_tier),
        kv('plan', 'Plan', account.plan_interval),
      ],
    },
    {
      key: 'rep-coverage',
      heading: 'Rep Coverage',
      fields: [
        kv('gtme-owner', 'GTME Owner', account.owner_name),
        kv('gtme-meeting', 'GTME Meeting', hostOf(account.owner_meeting_link), account.owner_meeting_link ?? undefined),
        kv('gtme-cust-mtg', 'GTME Cust. Mtg', hostOf(account.owner_customer_meeting_link), account.owner_customer_meeting_link ?? undefined),
        kv('claydr', 'ClayDR', account.claydr_name),
        kv('cdr-meeting', 'CDR Meeting', hostOf(account.claydr_meeting_link), account.claydr_meeting_link ?? undefined),
        kv('cdr-cust-mtg', 'CDR Cust. Mtg', hostOf(account.claydr_customer_meeting_link), account.claydr_customer_meeting_link ?? undefined),
        kv('gs', 'GS', account.gs_name),
      ],
    },
    {
      key: 'signals-engagement',
      heading: 'Signals & Engagement',
      fields: [
        kv('last-signal', 'Last Signal', account.lim_name, account.lim_link ?? undefined),
        kv('signal-date', 'Signal Date', fmtDate(account.lim_date)),
        kv('signal-type', 'Signal Type', joinNonBlank([account.lim_type, account.lim_subtype])),
        kv('l90-signals', 'L90 Signals', num(account.l90_signal_count)),
        kv('l90-web-visits', 'L90 Web Visits', num(account.l90_website_visits)),
        kv('l90-clay-users', 'L90 Clay Users', num(account.l90_active_clay_users)),
        kv('engagement-l90', 'Engagement (L90)', num(account.engagement_score_90_days)),
        kv('eng-status', 'Eng. Status', account.engagement_status_90_days),
        kv('eng-channels', 'Eng. Channels', account.engagement_channels),
        kv('workspaces', '# Workspaces', num(account.number_of_workspace)),
        kv('ws-created-l90', 'WS Created L90', num(account.workspaces_created_last_90_days)),
        kv('suppressed', 'Suppressed', yesNo(account.suppressed)),
      ],
    },
  ];

  // Workspace (U6, R4) — the parsed primary-workspace link (Decision 4).
  // Hidden entirely when the account has no primary workspace (fill rate
  // ~78%, plan Approach note) — matches every other conditional section.
  if (account.account_primary_workspace) {
    sections.push({
      key: 'workspace',
      heading: 'Workspace',
      fields: [
        kv('primary-workspace', 'Primary Workspace', hostOf(account.account_primary_workspace.url), account.account_primary_workspace.url),
        kv('workspace-tier', 'Workspace Tier', account.product_tier),
        kv('plan-interval', 'Plan Interval', account.plan_interval),
      ],
    });
  }

  return sections;
}

// ── `accountLongTextBlocks` — the full-width prose blocks below the grid ────

export interface AccountLongTextBlock {
  key: string;
  heading: string;
  text: string;
  html?: boolean;
}

/**
 * Description, rendered above the field sections (GTMOPS-2950). Null when
 * the account has none.
 */
export function accountDescriptionBlock(account: AccountRow): AccountLongTextBlock | null {
  return account.account_description
    ? { key: 'account-description', heading: 'Description', text: account.account_description }
    : null;
}

/**
 * Fit Score Summary, rendered under Description, above the field sections (GTMOPS-2950).
 * Null when the account has none.
 */
export function accountFitScoreBlock(account: AccountRow): AccountLongTextBlock | null {
  return account.fit_score_summary
    ? { key: 'fit-score-summary', heading: 'Fit Score Summary', text: account.fit_score_summary }
    : null;
}

/**
 * The full-width long-text blocks rendered below the two-column field grid,
 * in production order. Only Engagement Summary is rich HTML (sanitized by
 * the caller via `sanitizeRichHtml`, GTMOPS-2136); the rest are plain
 * strings. A null/empty source omits its block entirely.
 */
export function accountLongTextBlocks(account: AccountRow): AccountLongTextBlock[] {
  const blocks: AccountLongTextBlock[] = [];
  if (account.engagement_summary_90_days) {
    blocks.push({ key: 'engagement-summary', heading: 'Engagement Summary', text: account.engagement_summary_90_days, html: true });
  }
  if (account.tech_stack) {
    blocks.push({ key: 'tech-stack-first-party', heading: 'Tech Stack (first-party)', text: account.tech_stack });
  }
  if (account.technologies_used) {
    blocks.push({ key: 'tech-stack-scraped', heading: 'Tech Stack (scraped)', text: account.technologies_used });
  }
  if (account.lim_description) {
    blocks.push({ key: 'last-signal-description', heading: 'Last Signal Description', text: account.lim_description });
  }
  return blocks;
}
